import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  DATE_FORMAT,
  HighlightIcon,
  type HighlightIconVariant,
  Icon,
  type IconName,
  PageModal,
} from '@dev-spendesk/grapes';
import React, { useEffect } from 'react';

import { ContactUs } from 'common/components/ContactUs';
import { type I18nKey, useTranslation } from 'common/hooks/useTranslation';
import { type PriceMigrationPlan } from 'modules/company/billing/components/CompanyPlan/hooks/useGetBillingMigration/useGetBillingMigration';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  migrationPlan: PriceMigrationPlan;
  userId: string;
};

const reasonItems: { id: string; key: I18nKey }[] = [
  { id: 'reason1', key: 'billing.priceUpdateModal.whySection.reasons.item1' },
  { id: 'reason2', key: 'billing.priceUpdateModal.whySection.reasons.item2' },
  { id: 'reason3', key: 'billing.priceUpdateModal.whySection.reasons.item3' },
  { id: 'reason4', key: 'billing.priceUpdateModal.whySection.reasons.item4' },
  { id: 'reason5', key: 'billing.priceUpdateModal.whySection.reasons.item5' },
];

const comingNextItems: {
  id: string;
  iconName: IconName;
  iconVariant: HighlightIconVariant;
  title: I18nKey;
  description: I18nKey;
}[] = [
  {
    id: 'nextItem1',
    iconName: 'globe-eu-africa',
    iconVariant: 'pink',
    title: 'billing.priceUpdateModal.nextSection.items.item1.title',
    description: 'billing.priceUpdateModal.nextSection.items.item1.description',
  },
  {
    id: 'nextItem2',
    iconName: 'card',
    iconVariant: 'lemon',
    title: 'billing.priceUpdateModal.nextSection.items.item2.title',
    description: 'billing.priceUpdateModal.nextSection.items.item2.description',
  },
  {
    id: 'nextItem3',
    iconName: 'shopping-bag',
    iconVariant: 'blue',
    title: 'billing.priceUpdateModal.nextSection.items.item3.title',
    description: 'billing.priceUpdateModal.nextSection.items.item3.description',
  },
  {
    id: 'nextItem4',
    iconName: 'square-3-stack-3d',
    iconVariant: 'apricot',
    title: 'billing.priceUpdateModal.nextSection.items.item4.title',
    description: 'billing.priceUpdateModal.nextSection.items.item4.description',
  },
];

const faqItems: { id: string; question: I18nKey; answer: I18nKey }[] = [
  {
    id: 'faqItem1',
    question: 'billing.priceUpdateModal.faqSection.item1.question',
    answer: 'billing.priceUpdateModal.faqSection.item1.answer',
  },
  {
    id: 'faqItem2',
    question: 'billing.priceUpdateModal.faqSection.item2.question',
    answer: 'billing.priceUpdateModal.faqSection.item2.answer',
  },
  {
    id: 'faqItem3',
    question: 'billing.priceUpdateModal.faqSection.item3.question',
    answer: 'billing.priceUpdateModal.faqSection.item3.answer',
  },
  {
    id: 'faqItem4',
    question: 'billing.priceUpdateModal.faqSection.item4.question',
    answer: 'billing.priceUpdateModal.faqSection.item4.answer',
  },
  {
    id: 'faqItem5',
    question: 'billing.priceUpdateModal.faqSection.item5.question',
    answer: 'billing.priceUpdateModal.faqSection.item5.answer',
  },
  {
    id: 'faqItem6',
    question: 'billing.priceUpdateModal.faqSection.item6.question',
    answer: 'billing.priceUpdateModal.faqSection.item6.answer',
  },
];

export const PricingUpdateModal = ({
  isOpen,
  setIsOpen,
  migrationPlan,
  userId,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const changeDate = new Date(migrationPlan.migrationDate);

  useEffect(() => {
    if (isOpen) {
      track(AnalyticEventName.PRICE_UPDATE_MODAL_OPENED, { user_id: userId });
    }
  }, [isOpen]);

  return (
    <PageModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={
        <img
          src="/static/img/spendesk-black-logo.svg"
          width={124}
          alt="Spendesk logo"
          className="ml-12"
        />
      }
    >
      <div className="flex justify-center gap-48 px-[80px] py-16">
        <section className="w-[40vw] max-w-[1000px]">
          <h1 className="mb-20 title-xxl">
            {t('billing.priceUpdateModal.title')}
          </h1>
          <p className="body-l">
            {t('billing.priceUpdateModal.description', {
              date: localeFormat(changeDate, DATE_FORMAT.CUSTOM, {
                dateStyle: 'medium',
                timeZone: 'Europe/Paris',
              }),
            })}
            <span className="text-content-brand-default">
              {' '}
              {t('billing.priceUpdateModal.descriptionCommitment')}
            </span>
          </p>
          <section className="mt-24">
            <h2 className="mb-16 heading-l">
              {t('billing.priceUpdateModal.whySection.title')}
            </h2>
            <p className="mb-12 text-content-secondary-bg-secondary">
              {t('billing.priceUpdateModal.whySection.description')}
            </p>
            <ul>
              {reasonItems.map((item) => (
                <li key={item.id} className="mt-8 flex items-center">
                  <Icon
                    name="circle-check"
                    className="mr-12 shrink-0 text-content-success-default"
                  />
                  {t(item.key)}
                </li>
              ))}
            </ul>
          </section>
          <section className="mt-24">
            <h2 className="mb-16 heading-l">
              {t('billing.priceUpdateModal.nextSection.title')}
            </h2>
            <p className="mb-20 text-content-secondary-bg-secondary">
              {t('billing.priceUpdateModal.nextSection.description')}
            </p>
            {comingNextItems.map((item) => (
              <div
                key={item.id}
                className="box mt-16 flex items-center gap-16 p-16"
              >
                <HighlightIcon
                  name={item.iconName}
                  size={40}
                  variant={item.iconVariant}
                  className="shrink-0"
                />
                <div>
                  <span className="title-m">{t(item.title)}</span>
                  <p className="text-content-secondary-bg-secondary">
                    {t(item.description)}
                  </p>
                </div>
              </div>
            ))}
          </section>
          <section className="mt-24">
            <h2 className="mb-16 heading-l">
              {t('billing.priceUpdateModal.faqSection.title')}
            </h2>
            <Accordion>
              {faqItems.map((faqItem) => (
                <AccordionItem
                  key={faqItem.id}
                  renderHeader={() => (
                    <div className="title-m">{t(faqItem.question)}</div>
                  )}
                >
                  <div
                    className="px-24 py-12 text-content-secondary-bg-secondary"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: t(faqItem.answer) }}
                  />
                </AccordionItem>
              ))}
            </Accordion>
          </section>
          <div className="mt-24 flex items-center rounded-8 bg-background-secondary-default p-16">
            <p className="title-m">
              {t('billing.priceUpdateModal.otherQuestion')}
            </p>
            <ContactUs>
              {({ handleContactUs }) => (
                <Button
                  className="ml-auto whitespace-nowrap"
                  variant="secondaryNeutral"
                  text={t('helpCenter.contactUs')}
                  onClick={() => {
                    handleContactUs();
                  }}
                />
              )}
            </ContactUs>
          </div>
        </section>
        <section className="sticky top-0 h-fit w-[480px] min-w-[480px] rounded-8 bg-background-secondary-default p-24">
          <h3 className="mb-20 title-l">{t('misc.details')}</h3>
          <div className="mb-20 flex justify-between">
            <span className="text-content-secondary-bg-secondary body-m">
              {t('billing.priceUpdateModal.currentPrice')}
            </span>
            <span className="font-bold title-xl">
              {formatMonetaryValue(migrationPlan.current)}
            </span>
          </div>
          <div className="mb-20 flex justify-between">
            <span className="text-content-secondary-bg-secondary">
              {t('billing.priceUpdateModal.newPrice')}
            </span>
            <span className="font-bold title-xl">
              {formatMonetaryValue(migrationPlan.estimate)}
            </span>
          </div>
          {migrationPlan.planItems && migrationPlan.planItems.length > 0 && (
            <Box
              variant="primary"
              className="mb-24 max-h-[250px] overflow-auto"
            >
              <div className="mb-16 text-content-primary title-m">
                {t('billing.priceUpdateModal.entityBreakdown')}
              </div>
              <ul className="flex flex-col gap-8">
                {migrationPlan.planItems.map((planItem) => (
                  <li
                    key={planItem.companyId}
                    className="flex justify-between gap-4"
                  >
                    <span className="overflow-hidden text-ellipsis text-nowrap text-content-secondary-bg-primary">
                      {planItem.companyName}
                    </span>
                    <span className="title-l">
                      {formatMonetaryValue(planItem.estimate)}
                    </span>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          <div>
            <span className="title-m">
              {t('billing.priceUpdateModal.note')}
            </span>
            <ul className="mt-8 list-disc px-24 text-content-secondary-bg-secondary">
              <li>{t('billing.priceUpdateModal.priceIsEstimate')}</li>
              <li>{t('billing.priceUpdateModal.entitiesPrice')}</li>
              <li>{t('billing.priceUpdateModal.priceIsWithoutVat')}</li>
              {migrationPlan.planItems &&
                migrationPlan.planItems.length > 0 && (
                  <li>{t('billing.priceUpdateModal.conversion')}</li>
                )}
            </ul>
          </div>
          <div className="separator my-20" />
          <span className="title-m">
            {t('billing.priceUpdateModal.timeline')}
          </span>
          <div className="mt-8 flex justify-between gap-16">
            <Icon
              name="calendar-clock"
              size="l"
              className="text-content-brand-default"
            />
            <div className="w-fit">
              <span className="title-m">
                {localeFormat(changeDate, DATE_FORMAT.CUSTOM, {
                  dateStyle: 'short',
                  timeZone: 'Europe/Paris',
                })}
              </span>
              <p className="mt-8 text-content-secondary-bg-secondary">
                {t('billing.priceUpdateModal.newPriceDate')}
              </p>
            </div>
          </div>
        </section>
      </div>
    </PageModal>
  );
};
